/**
 * Slide-over drawer: shows all cached ETFs of a given taxonomy bucket,
 * sorted by AUM desc. Opened by clicking an EtfTypeChip.
 *
 * Props
 *   typeId          – string id, e.g. "covered_call" (null = closed)
 *   onClose         – () => void
 *   onPickTicker    – (symbol) => void   pre-loads ticker in the tracker
 */
import { useEffect, useRef, useState } from "react";
import { X, Loader2, ArrowUpRight } from "lucide-react";
import { getEtfsByType } from "@/lib/api";

const fmtAum = (n) => {
  if (!n || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(0)}M`;
  return n.toString();
};

export default function EtfsByTypeDrawer({ typeId, onClose, onPickTicker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const closeBtnRef = useRef(null);

  // Fetch when opened
  useEffect(() => {
    if (!typeId) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setData(null);
    getEtfsByType(typeId)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData({ count: 0, rows: [] }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [typeId]);

  // Esc to close + scroll-lock body + focus the close button
  useEffect(() => {
    if (!typeId) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [typeId, onClose]);

  if (!typeId) return null;

  const meta = data?.type;
  const rows = data?.rows || [];

  return (
    <div
      data-testid="etfs-by-type-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={`ETFs in category ${meta?.label || typeId}`}
      className="fixed inset-0 z-50"
    >
      {/* Scrim */}
      <button
        data-testid="etfs-by-type-scrim"
        onClick={onClose}
        aria-label="Close panel"
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px] cursor-default"
      />

      {/* Panel */}
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[460px] md:w-[520px] bg-[#0e0e10] border-l border-zinc-800 shadow-2xl flex flex-col"
        style={{
          animation: "slideInRight 220ms cubic-bezier(.22,.61,.36,1) both",
        }}
      >
        {/* Header */}
        <header className="px-5 sm:px-7 pt-6 pb-4 border-b border-zinc-900">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-label mb-1.5 flex items-center gap-2">
                {meta?.color && (
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: meta.color }}
                  />
                )}
                <span>{meta?.label || typeId}</span>
              </div>
              <h2
                data-testid="etfs-by-type-title"
                className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-100"
              >
                {data ? `${data.count} ETF${data.count === 1 ? "" : "s"} in this category` : "Loading…"}
              </h2>
              {meta?.blurb && (
                <p className="mt-2 text-xs sm:text-sm leading-relaxed text-zinc-400 normal-case tracking-normal">
                  {meta.blurb}
                </p>
              )}
            </div>
            <button
              ref={closeBtnRef}
              data-testid="etfs-by-type-close"
              onClick={onClose}
              className="shrink-0 p-2 -m-2 text-zinc-500 hover:text-zinc-100 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center h-32 text-xs font-mono text-zinc-500">
              <Loader2 size={14} className="animate-spin mr-2" /> Loading ETFs…
            </div>
          )}

          {!loading && rows.length === 0 && (
            <div className="px-5 sm:px-7 py-10 text-center">
              <div className="text-label mb-2">No ETFs cached yet</div>
              <p className="text-xs text-zinc-500 normal-case tracking-normal">
                The daily seed job pre-warms the popular ETF universe each morning. Check back soon.
              </p>
            </div>
          )}

          {!loading && rows.length > 0 && (
            <ol data-testid="etfs-by-type-list" className="divide-y divide-zinc-900">
              {rows.map((r, idx) => (
                <li key={r.symbol}>
                  <button
                    data-testid={`etfs-by-type-row-${r.symbol}`}
                    onClick={() => {
                      onPickTicker?.(r.symbol);
                      onClose?.();
                    }}
                    className="w-full px-5 sm:px-7 py-4 grid grid-cols-[auto_1fr_auto] items-center gap-4 text-left hover:bg-zinc-900/40 transition-colors normal-case tracking-normal"
                  >
                    <span className="font-mono text-[10px] text-zinc-600 tabular-nums w-6">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-mono font-bold text-zinc-100">{r.symbol}</span>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                          {r.family || r.category || "ETF"}
                        </span>
                      </div>
                      <div className="text-sm text-zinc-300 truncate">{r.name}</div>
                      {r.tagline && (
                        <div className="text-[11px] italic text-zinc-500 truncate mt-0.5">
                          {r.tagline}
                        </div>
                      )}
                    </span>
                    <span className="text-right">
                      <div className="font-mono text-sm text-zinc-100 tabular-nums">
                        ${fmtAum(r.aum)}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                        {r.expense_ratio != null ? `${Number(r.expense_ratio).toFixed(2)}% ER` : "AUM"}
                      </div>
                      <ArrowUpRight size={11} className="text-zinc-600 inline mt-1" />
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>

        <footer className="px-5 sm:px-7 py-3 border-t border-zinc-900 text-[10px] font-mono uppercase tracking-widest text-zinc-600 flex items-center justify-between">
          <span>Sorted by AUM · Cached daily</span>
          <span>Press Esc to close</span>
        </footer>
      </aside>
    </div>
  );
}
